import {
  Dropbox,
  DropboxAuth,
} from 'dropbox';

import { Resources, TEXT_IDS } from '../../../resources';
import { settings } from '../../../settings';
import { uuidv4 } from '../../../util';
import * as LOG from '../../../log'

class WrcDropbox {
  static CLIENT_ID = "xvz6ybh9wta2u30";
  static PREFIX = "dropbox.";
  static CODE_VERIFIER_PROP = `${this.PREFIX}verifier`;
  static ERROR_DESC_PROP = `${this.PREFIX}errorDesc`;
  static CODE_PROP = `${this.PREFIX}code`;
  static RETURN_URI = `${this.PREFIX}returnUri`;
  static REDIRECT_URIS = [{
    uri: "http://localhost:3000",
    redirectUri: "http://localhost:3000/dropbox/"
  }, {
    uri: "http://localhost:3200",
    redirectUri: "http://localhost:3200/dropbox/"
  }, {
    uri: "http://localhost:8000",
    redirectUri: "http://localhost:8000/dropbox/"
  }, {
    uri: "http://localhost:8000/app/editor",
    redirectUri: "http://localhost:8000/app/editor/dropbox/"
  }, {
    uri: "http://localhost:8000/app/standalone",
    redirectUri: "http://localhost:8000/app/standalone/dropbox/"
  }, {
    uri: "https://play.webrcade.com",
    redirectUri: "https://play.webrcade.com/dropbox/"
  }, {
    uri: "https://play.webrcade.com/app/editor",
    redirectUri: "https://play.webrcade.com/app/editor/dropbox/"
  }, {
    uri: "https://play.webrcade.com/app/standalone",
    redirectUri: "https://play.webrcade.com/app/standalone/dropbox/"
  }, {
    uri: "https://play-staging.webrcade.com",
    redirectUri: "https://play-staging.webrcade.com/dropbox/"
  }, {
    uri: "https://play-staging.webrcade.com/app/editor",
    redirectUri: "https://play-staging.webrcade.com/app/editor/dropbox/"
  }, {
    uri: "https://play-staging.webrcade.com/app/standalone",
    redirectUri: "https://play-staging.webrcade.com/app/standalone/dropbox/"
  }, {
    uri: "https://play.webrcade.com/app/standalone",
    redirectUri: "https://play.webrcade.com/app/standalone/dropbox/"
  }, {
    uri: "https://webrcade.github.io/webrcade-mirror",
    redirectUri: "https://webrcade.github.io/webrcade-mirror/dropbox/"
  }, {
    uri: "https://webrcade.github.io/webrcade-mirror/app/editor",
    redirectUri: "https://webrcade.github.io/webrcade-mirror/app/editor/dropbox/"
  }, {
    uri: "https://webrcade.github.io/webrcade-mirror/app/standalone",
    redirectUri: "https://webrcade.github.io/webrcade-mirror/app/standalone/dropbox/"
  }, {
    uri: "https://play-staging-flare.webrcade.com",
    redirectUri: "https://play-staging-flare.webrcade.com/dropbox/"
  }, {
    uri: "https://play-staging-flare.webrcade.com/app/editor",
    redirectUri: "https://play-staging-flare.webrcade.com/app/editor/dropbox/"
  }, {
    uri: "https://play-staging-flare.webrcade.com/app/standalone",
    redirectUri: "https://play-staging-flare.webrcade.com/app/standalone/dropbox/"
  }, {
  }];

  constructor() {
    this.dbx = null;
  }

  clearSession() {
    Object.keys(sessionStorage).forEach(function (key) {
      if (key.startsWith(WrcDropbox.PREFIX)) {
        sessionStorage.removeItem(key);
      }
    });
  }

  getRedirectUri() {
    let redirectUri = null;
    let uriLen = 0;
    const currentUri = window.location.href.split('?')[0].toLowerCase();
    for (let i = 0; i < WrcDropbox.REDIRECT_URIS.length; i++) {
      const redir = WrcDropbox.REDIRECT_URIS[i];
      const uri = redir.uri;
      const redirUri = redir.redirectUri;
      if (currentUri.startsWith(uri)) {
        const len = uri.length;
        if (!redirectUri || (len > uriLen)) {
          redirectUri = redirUri;
          uriLen = len;
        }
      }
    }

    return redirectUri;
  }

  async getDropbox() {
    if (!this.dbx) {
      const token = settings.getDbToken();
      if (token) {
        const dbxAuth = new DropboxAuth({
          clientId: WrcDropbox.CLIENT_ID,
        });
        dbxAuth.setRefreshToken(token);
        this.dbx = new Dropbox({
          auth: dbxAuth
        });
      } else {
        throw "Dropbox token is not available.";
      }
    }
    if (this.dbx) {
      await this.dbx.auth.checkAndRefreshAccessToken();
    }
    return this.dbx;
  }

  async checkFileExists(path) {
    let exists = false;

    try {
      const dbx = await this.getDropbox();
      await dbx.filesGetMetadata({path: path});
      exists = true;
    } catch (e) {
      LOG.error(e);
    }
    return exists;
  }

  async deleteFile(path) {
    let ret = false;
    try {
      const dbx = await this.getDropbox();
      await dbx.filesDeleteV2({path: path});
      ret = true;
    } catch (e) {
      LOG.error(e);
    }
    return ret;
  }

  async listFolder(path) {
    const dbx = await this.getDropbox();
    const result = await dbx.filesListFolder({
        path: path,
        limit: 2000,
        include_non_downloadable_files: false
      }
    );

    return result;
  }

  async createSharedLink(path) {
    const dbx = await this.getDropbox();
    const result = await dbx.sharingCreateSharedLinkWithSettings({
        path: path,
      }
    );

    return result;
  }

  async downloadFile(path) {
    const dbx = await this.getDropbox();
    const linkResult = await dbx.filesGetTemporaryLink({path: path});
    const response = await fetch(linkResult.result.link);
    if (!response.ok) throw new Error(`Dropbox download failed: ${response.status}`);
    return await response.blob();
  }

  httpHeaderSafeJson(v) {
    const charsToEncode = /[\u007f-\uffff]/g;
    return JSON.stringify(v).replace(charsToEncode, function(c) {
      return '\\u' + ('000' + c.charCodeAt(0).toString(16)).slice(-4);
    });
  }

  async uploadFile(blob, path, onProgress) {
    const UPLOAD_FILE_SIZE_LIMIT = 150 * 1024 * 1024;

    const dbx = await this.getDropbox();
    const file = new File([blob], path);

    if (file.size < UPLOAD_FILE_SIZE_LIMIT) {
      if (onProgress) { // Use XHR so progress events fire
        const token = dbx.auth.getAccessToken();
        const xhrUpload = () => new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', 'https://content.dropboxapi.com/2/files/upload');
          xhr.setRequestHeader('Authorization',   `Bearer ${token}`);
          xhr.setRequestHeader('Content-Type',    'application/octet-stream');
          xhr.setRequestHeader('Dropbox-API-Arg', this.httpHeaderSafeJson({
            path: file.name,
            mode:       'overwrite',
            autorename: false,
            mute:       false,
          }));
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
          };
          xhr.onload  = () => (xhr.status === 200 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`)));
          xhr.onerror = () => reject(new Error('Upload network error'));
          xhr.onabort = () => reject(new Error('Upload cancelled'));
          xhr.send(file);
        });
        // Retry on CORS preflight failures — failed preflights are not cached by browsers
        // so a retry will trigger a fresh OPTIONS request which usually succeeds
        for (let attempt = 1; attempt <= 5; attempt++) {
          try {
            await xhrUpload();
            break;
          } catch (e) {
            if (attempt === 5) throw e;
            LOG.error(`Upload attempt ${attempt} failed, retrying in ${500 * attempt}ms: ${e.message}`);
            await new Promise(r => setTimeout(r, 500 * attempt));
          }
        }
        return true;
      } else { // No progress needed — use the SDK
        for (let attempt = 1; attempt <= 5; attempt++) {
          try {
            await dbx.filesUpload({
              path: file.name,
              contents: file,
              mode: 'overwrite'
            });
            break;
          } catch (e) {
            if (attempt === 5) throw e;
            LOG.error(`Upload attempt ${attempt} failed, retrying in ${500 * attempt}ms: ${e.message}`);
            await new Promise(r => setTimeout(r, 500 * attempt));
          }
        }
        return true;
      }
    } else { // File is bigger than 150 Mb - use filesUploadSession* API
      const maxBlob = 8 * 1000 * 1000; // 8Mb - Dropbox JavaScript API suggested max file / chunk size
      var offset = 0;

      onProgress?.(0);

      // Create array of work items
      let sessionId = null;
      let idx = 0;
      while (offset < file.size) {
        const chunkSize = Math.min(maxBlob, file.size - offset);
        const blob = file.slice(offset, offset + chunkSize);
        if (offset === 0) {
          const response = await dbx.filesUploadSessionStart({ close: false, contents: blob });
          sessionId = response.result.session_id;
        } else if ((offset + chunkSize) === file.size) {
          const cursor = { session_id: sessionId, offset: file.size - blob.size };
          const commit = { path: file.name, mode: 'overwrite', autorename: true, mute: false };
          await dbx.filesUploadSessionFinish({ cursor: cursor, commit: commit, contents: blob });
          onProgress?.(100);
          return true;
        } else {
          const cursor = { session_id: sessionId, offset: idx * maxBlob };
          await dbx.filesUploadSessionAppendV2({ cursor: cursor, close: false, contents: blob })
        }
        offset += chunkSize;
        idx++;
        onProgress?.(Math.round((offset / file.size) * 100));
      }
      return false;
    }
  }

  async testWrite() {
    const path = `/_test_/${uuidv4()}`;
    const val = 123;
    try {
      let bytes = new Uint8Array(1);
      bytes[0] = val;
      await this.uploadFile(new Blob([bytes]), path);
      const blob = await this.downloadFile(path);
      const arrayBuffer = await blob.arrayBuffer();
      this.deleteFile(path);
      return new Uint8Array(arrayBuffer)[0] === val;
    } catch(e) {
      LOG.error(e);
    }
    return false;
  }

  async checkLinkResult() {
    try {
      const errorDesc = sessionStorage.getItem(WrcDropbox.ERROR_DESC_PROP);
      if (errorDesc) {
        throw errorDesc;
      }

      const verifier = window.sessionStorage.getItem(WrcDropbox.CODE_VERIFIER_PROP);
      const code = window.sessionStorage.getItem(WrcDropbox.CODE_PROP);
      const redirectUri = this.getRedirectUri();

      if (verifier && code && redirectUri) {
        const dbxAuth = new DropboxAuth({
          clientId: WrcDropbox.CLIENT_ID,
        });
        dbxAuth.setCodeVerifier(verifier);

        const response = await dbxAuth.getAccessTokenFromCode(redirectUri, code);
        const refreshToken = response.result.refresh_token;

        settings.setDbToken(refreshToken);
        await settings.save();
      }
    } finally {
      this.clearSession();
    }
  }

  async link(returnUri) {
    this.clearSession();

    const dbxAuth = new DropboxAuth({
      clientId: WrcDropbox.CLIENT_ID,
    });

    const redirectUri = this.getRedirectUri();
    if (!redirectUri) {
      throw Resources.getText(TEXT_IDS.DROPBOX_SERVER_UNAUTHORIZED);
    }
    const authUrl = await dbxAuth.getAuthenticationUrl(redirectUri, undefined, 'code', 'offline', undefined, undefined, true);
    window.sessionStorage.setItem(WrcDropbox.CODE_VERIFIER_PROP, dbxAuth.codeVerifier);
    if (returnUri) {
      window.sessionStorage.setItem(WrcDropbox.RETURN_URI, returnUri);
    }
    window.location.href = authUrl;
  }
}

const dropbox = new WrcDropbox();

export { dropbox }
